import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Download,
  Edit2,
  FileText,
  MoreVertical,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/useAuth";
import { useNotify } from "@/hooks/useNotify";
import {
  getExpiryStatus,
  SUPPLIER_DOCUMENT_TYPE_LABELS,
  type SupplierDocumentType,
} from "./supplierDocumentTypes";
import SupplierDocumentUploadDialog, {
  type SupplierDocumentRecord,
} from "./SupplierDocumentUploadDialog";

interface Props {
  supplierId: string;
}

interface DocRow extends SupplierDocumentRecord {
  file_size_bytes: number | null;
  mime_type: string | null;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

const BUCKET = "supplier-documents";

type FilterMode = "all" | "expiring" | "expired";

const SupplierDocuments: React.FC<Props> = ({ supplierId }) => {
  const { user } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierDocumentRecord | null>(null);
  const [replacingFor, setReplacingFor] = useState<SupplierDocumentRecord | null>(null);
  const [deleting, setDeleting] = useState<DocRow | null>(null);
  const [filter, setFilter] = useState<FilterMode>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["supplier-documents", supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_documents")
        .select("*")
        .eq("supplier_id", supplierId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DocRow[];
    },
  });

  const docs = data ?? [];

  const filtered = useMemo(() => {
    if (filter === "all") return docs;
    return docs.filter((d) => {
      const s = getExpiryStatus(d.expiry_date);
      return filter === "expired" ? s === "expired" : s === "expiring";
    });
  }, [docs, filter]);

  const counts = useMemo(() => {
    let expiring = 0;
    let expired = 0;
    for (const d of docs) {
      const s = getExpiryStatus(d.expiry_date);
      if (s === "expiring") expiring++;
      else if (s === "expired") expired++;
    }
    return { expiring, expired };
  }, [docs]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["supplier-documents", supplierId] });

  const handleDownload = async (doc: DocRow) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(doc.storage_path, 60);
    if (error || !data) {
      notify.error(error?.message ?? "Could not generate download link");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const { error } = await supabase
      .from("supplier_documents")
      .delete()
      .eq("id", deleting.id);
    if (error) {
      notify.error(error);
      return;
    }
    await supabase.storage.from(BUCKET).remove([deleting.storage_path]);
    notify.success("Document deleted");
    setDeleting(null);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
            All ({docs.length})
          </FilterChip>
          <FilterChip
            active={filter === "expiring"}
            onClick={() => setFilter("expiring")}
            disabled={counts.expiring === 0}
          >
            Expiring soon ({counts.expiring})
          </FilterChip>
          <FilterChip
            active={filter === "expired"}
            onClick={() => setFilter("expired")}
            disabled={counts.expired === 0}
          >
            Expired ({counts.expired})
          </FilterChip>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setReplacingFor(null);
            setUploadOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload document
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground border border-dashed rounded-md">
          {docs.length === 0
            ? "No documents uploaded yet for this supplier."
            : "No documents match the selected filter."}
        </div>
      ) : (
        <ul className="divide-y rounded-md border">
          {filtered.map((doc) => {
            const status = getExpiryStatus(doc.expiry_date);
            const canManage = user?.id === doc.uploaded_by;
            return (
              <li
                key={doc.id}
                className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">
                        {SUPPLIER_DOCUMENT_TYPE_LABELS[doc.document_type]}
                      </Badge>
                      {status && <StatusPill status={status} />}
                    </div>
                    <button
                      type="button"
                      className="mt-1 block text-sm font-medium text-left hover:underline truncate max-w-full"
                      onClick={() => navigate(`/documents/supplier/${doc.id}`)}
                    >
                      {doc.file_name}
                    </button>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {doc.issue_date
                        ? `Issued ${format(new Date(doc.issue_date), "dd MMM yyyy")}`
                        : "No issue date"}
                      {" · "}
                      {doc.expiry_date
                        ? `Expires ${format(new Date(doc.expiry_date), "dd MMM yyyy")}`
                        : "No expiry"}
                    </p>
                    {doc.notes && (
                      <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                        {doc.notes}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleDownload(doc)}>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </DropdownMenuItem>
                      {canManage && (
                        <>
                          <DropdownMenuItem
                            onClick={() => {
                              setEditing(doc);
                              setReplacingFor(null);
                              setUploadOpen(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setReplacingFor(doc);
                              setEditing(null);
                              setUploadOpen(true);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Replace file
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleting(doc)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <SupplierDocumentUploadDialog
        open={uploadOpen}
        onOpenChange={(o) => {
          setUploadOpen(o);
          if (!o) {
            setEditing(null);
            setReplacingFor(null);
          }
        }}
        supplierId={supplierId}
        editing={editing}
        replacingFor={replacingFor}
        onSuccess={refresh}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              The file will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const FilterChip: React.FC<{
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, disabled, onClick, children }) => (
  <Button
    type="button"
    size="sm"
    variant={active ? "default" : "outline"}
    onClick={onClick}
    disabled={disabled}
    className="h-7"
  >
    {children}
  </Button>
);

const StatusPill: React.FC<{ status: "expired" | "expiring" | "valid" }> = ({ status }) => {
  if (status === "expired") {
    return (
      <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15 border-destructive/30">
        Expired
      </Badge>
    );
  }
  if (status === "expiring") {
    return (
      <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15 border-amber-500/30">
        Expiring soon
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 border-emerald-500/30">
      Valid
    </Badge>
  );
};

export function useSupplierDocumentSummary(supplierId: string) {
  const { data } = useQuery({
    queryKey: ["supplier-documents-summary", supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_documents")
        .select("expiry_date")
        .eq("supplier_id", supplierId);
      if (error) throw error;
      let expiring = 0;
      let expired = 0;
      for (const d of data ?? []) {
        const s = getExpiryStatus(d.expiry_date);
        if (s === "expiring") expiring++;
        else if (s === "expired") expired++;
      }
      return { total: data?.length ?? 0, expiring, expired };
    },
  });
  return data ?? { total: 0, expiring: 0, expired: 0 };
}

export default SupplierDocuments;
